import React, { useState, useMemo } from 'react'
import { Box, Button, Grid2, Typography } from '@mui/material'
import { SearchSection } from '../../student/MyCourses'
import CustomDivider from '../../../shared/utils/CustomDivider'
import CountryTableList from './CountryTableList'
import { NormalContainerWithHeader } from '../../../shared/components/student/NormalComponent'
import SearchInput from '../../../shared/components/student/SearchInput'
import { useAdminCountry, useAllAdminCountry } from '../../../assets/api/useAdminApi'
import CountryForm from './CountryForm'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToaster } from '../../../shared/utils/ToasterProvider'
import { useDialog } from '../../../shared/hook/useDialog'
import MainDialogBox from '../../../shared/components/Dialog/MainDialogBox'
import AdminToolbar from '../components/AdminToolbar'
import api from '../../../assets/nodeApi/axiosInstance'

const Country = () => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryIdToDelete, setCountryIdToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  const { open: deleteOpen, handleClickOpen: handleDeleteOpen, handleClose: handleDeleteClose } = useDialog();
  const { data: countryList, isLoading } = useAllAdminCountry();
 

  const deleteCountryMutation = useMutation({
    mutationFn: (countryId) => api.delete(`country-delete/${countryId}`),
    onSuccess: (response) => {
      showToaster("Country deleted successfully!", '', 'success');
      queryClient.invalidateQueries({ queryKey: ["countries-all-list"] });
      handleDeleteClose();
    },
    onError: (error) => {
      showToaster(error?.data?.error || "Failed to delete country", '', 'error');
      handleDeleteClose();
    }
  });

  const originalCountryRows = useMemo(() => {
    return countryList?.data?.map((item, index) => ({
      sr_no: index + 1,
      ...item
    })) || [];
  }, [countryList]);

  // Filter countries based on search query
  const filteredCountryRows = useMemo(() => {
    if (!searchQuery.trim()) {
      return originalCountryRows;
    }

    const lowercasedQuery = searchQuery.toLowerCase();
    return originalCountryRows.filter(country =>
      Object.values(country).some(val =>
        val != null && val.toString().toLowerCase().includes(lowercasedQuery)
      )
    );
  }, [originalCountryRows, searchQuery]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleEdit = (country) => {
    setSelectedCountry(country);
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (countryId) => {
    setCountryIdToDelete(countryId);
    handleDeleteOpen();
  };

  const confirmDelete = () => {
    if (countryIdToDelete) {
      deleteCountryMutation.mutate(countryIdToDelete);
    }
  };

  const handleFormSuccess = () => {
    setIsEditing(false);
    setSelectedCountry(null);
  };

  return (
    <Box>
      
      <CustomDivider />
      <NormalContainerWithHeader headerText={isEditing ? "Edit Country" : "Add Country"}>
        <CountryForm
          initialCountry={selectedCountry}
          isEditing={isEditing}
          onSuccess={handleFormSuccess}
        />
      </NormalContainerWithHeader>
      <CustomDivider />
      <NormalContainerWithHeader headerText={"Country List"} RightContent={
        <AdminToolbar
          onSearchChange={handleSearchChange}
        />
      }>
        <CountryTableList
          countryRows={filteredCountryRows}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </NormalContainerWithHeader>

      {/* Delete Confirmation Dialog */}
      <MainDialogBox open={deleteOpen} handleClose={handleDeleteClose} width={"40%"} >
        <Typography variant="h3" fontWeight={"bold"} mb={2}>Confirm Delete</Typography>
        <Typography variant="body1" mb={3}>Are you sure you want to delete this country?</Typography>
        <Grid2 container spacing={2} justifyContent="flex-end">
          <Grid2 children>
            <Button variant="outlined" onClick={handleDeleteClose}>Cancel</Button>
          </Grid2>
          <Grid2 children>
            <Button
              variant="contained"
              color="error"
              onClick={confirmDelete}
              disabled={deleteCountryMutation.isLoading}
            >
              {deleteCountryMutation.isLoading ? "Deleting..." : "Delete"}
            </Button>
          </Grid2>
        </Grid2>
      </MainDialogBox>
    </Box>
  )
}

export default Country